import React, { useState, useEffect } from 'react';
import { getModelPriceTrends } from '../../services/modelService';
import { Spinner } from '../common/Spinner';

interface ModelPriceTrendsProps {
  darkMode?: boolean;
  provider?: string | null;
  days?: number;
  className?: string;
}

export const ModelPriceTrends: React.FC<ModelPriceTrendsProps> = ({
  darkMode = false,
  provider = null,
  days = 30,
  className = ''
}) => {
  const [trends, setTrends] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(provider);
  const [timeRange, setTimeRange] = useState<number>(days);
  // Add sorting state
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  useEffect(() => {
    const fetchTrends = async () => {
      setLoading(true);
      try {
        const data = await getModelPriceTrends(selectedProvider, timeRange);
        setTrends(data);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch price trends');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrends();
  }, [selectedProvider, timeRange]);
  
  // Handle sort click
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Render sort indicator
  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };
  
  if (loading) return <div className="flex justify-center p-4"><Spinner size="md" /></div>;
  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  // Add this check for insufficient data
  if (!trends || trends.insufficient_data) {
    return (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border'} ${className}`}>
        <h3 className="text-lg font-medium mb-2">Price Changes (Last {timeRange} Days)</h3>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <p>{trends?.message || "No price trend data available for the selected criteria."}</p>
          <p className="mt-2">Suggestions:</p>
          <ul className="list-disc pl-5 mt-1">
            <li>Try increasing the time range</li>
            <li>Remove provider filters</li>
            <li>Wait for more historical data to be collected</li>
          </ul>
        </div>
      </div>
    );
  }

  if (Object.keys(trends.trends || {}).length === 0) {
    return (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border'} ${className}`}>
        <p>No price trend data available for the selected criteria.</p>
      </div>
    );
  }
  
  // Format data for display and apply sorting
  const trendData = Object.entries(trends.trends).map(([modelId, data]: [string, any]) => {
    const modelName = modelId.split('/')[1] || modelId;
    
    // Format prices for display (convert millicents to dollars per million tokens)
    const formatPrice = (millicents: number) => {
      // Convert from millicents per 1K tokens to dollars per 1M tokens
      // millicents per 1K tokens * 1000 = millicents per 1M tokens
      // millicents / 100,000 = dollars
      const dollarsPerMillion = millicents * 0.01;
      
      // Format based on the value range, preserving negative signs
      if (dollarsPerMillion === 0) {
        return '$0.00';
      } else if (Math.abs(dollarsPerMillion) < 0.01) {
        return `${dollarsPerMillion < 0 ? '-' : ''}$${Math.abs(dollarsPerMillion).toFixed(6)}`;  // Show more decimal places for very small values
      } else if (Math.abs(dollarsPerMillion) < 1) {
        return `${dollarsPerMillion < 0 ? '-' : ''}$${Math.abs(dollarsPerMillion).toFixed(4)}`;  // Show 4 decimal places for small values
      } else {
        return `${dollarsPerMillion < 0 ? '-' : ''}$${Math.abs(dollarsPerMillion).toFixed(2)}`;  // Show 2 decimal places for larger values
      }
    };
    
    return {
      id: modelId,
      name: modelName,
      provider: modelId.split('/')[0] || 'unknown',
      firstDate: data.first_date,
      lastDate: data.last_date,
      promptChange: data.prompt_cost_change_percent,
      completionChange: data.prompt_cost_change_percent,
      // Add raw values for debugging and display
      firstPromptCost: data.first_prompt_cost,
      lastPromptCost: data.last_prompt_cost,
      firstCompletionCost: data.first_completion_cost,
      lastCompletionCost: data.last_completion_cost,
      // Format prices for display
      firstPromptPrice: formatPrice(data.first_prompt_cost),
      lastPromptPrice: formatPrice(data.last_prompt_cost),
      firstCompletionPrice: formatPrice(data.first_completion_cost),
      lastCompletionPrice: formatPrice(data.last_completion_cost),
      isNewModel: data.is_new_model,
      promptAbsoluteChange: data.prompt_cost_change,
      completionAbsoluteChange: data.completion_cost_change,
      dataPoints: data.data_points,
      history: data.history
    };
  }).sort((a, b) => {
    let aValue, bValue;
    
    // Special handling for different fields
    switch (sortField) {
      case 'promptChange':
        aValue = parseFloat(a.promptChange);
        bValue = parseFloat(b.promptChange);
        break;
      case 'completionChange':
        aValue = parseFloat(a.completionChange);
        bValue = parseFloat(b.completionChange);
        break;
      case 'lastPromptPrice':
        // Sort by the raw numeric value, not the formatted string
        aValue = a.lastPromptCost;
        bValue = b.lastPromptCost;
        break;
      case 'lastCompletionPrice':
        // Sort by the raw numeric value, not the formatted string
        aValue = a.lastCompletionCost;
        bValue = b.lastCompletionCost;
        break;
      default:
        aValue = a[sortField];
        bValue = b[sortField];
    }
    
    // Handle numeric fields
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Handle string fields
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    }
    
    return 0;
  });
  
  // Get unique providers for filter
  const providers = ['all', ...new Set(trendData.map(item => item.provider))];
  
  return (
    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border'} ${className}`}>
      <div className="flex flex-wrap justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Price Changes (Last {timeRange} Days)</h3>
        
        <div className="flex space-x-2 mt-2 sm:mt-0">
          <select 
            value={selectedProvider || 'all'} 
            onChange={(e) => setSelectedProvider(e.target.value === 'all' ? null : e.target.value)}
            className={`px-3 py-1 rounded ${
              darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'
            } border`}
          >
            {providers.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className={`px-3 py-1 rounded ${
              darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'
            } border`}
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
          </select>
        </div>
      </div>
      
      {trends.anomaly_count > 0 && (
        <div className={`mb-4 p-3 rounded ${darkMode ? 'bg-yellow-800 text-yellow-100' : 'bg-yellow-100 text-yellow-800'}`}>
          <p className="text-sm">
            <strong>Note:</strong> Some unusual price changes were detected and have been adjusted for display.
            This may happen with newly introduced models or when prices change significantly.
          </p>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className={`min-w-full ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          <thead>
            <tr className={darkMode ? 'border-gray-700' : 'border-gray-200'}>
              <th 
                className="text-left py-2 cursor-pointer"
                onClick={() => handleSort('name')}
              >
                Model{renderSortIndicator('name')}
              </th>
              <th 
                className="text-left py-2 cursor-pointer"
                onClick={() => handleSort('provider')}
              >
                Provider{renderSortIndicator('provider')}
              </th>
              <th 
                className="text-right py-2 cursor-pointer"
                onClick={() => handleSort('lastPromptPrice')}
              >
                Current Input{renderSortIndicator('lastPromptPrice')}
              </th>
              <th 
                className="text-right py-2 cursor-pointer"
                onClick={() => handleSort('lastCompletionPrice')}
              >
                Current Output{renderSortIndicator('lastCompletionPrice')}
              </th>
              <th 
                className="text-right py-2 cursor-pointer"
                onClick={() => handleSort('promptChange')}
              >
                Input Change{renderSortIndicator('promptChange')}
              </th>
              <th 
                className="text-right py-2 cursor-pointer"
                onClick={() => handleSort('completionChange')}
              >
                Output Change{renderSortIndicator('completionChange')}
              </th>
              <th 
                className="text-right py-2 cursor-pointer"
                onClick={() => handleSort('dataPoints')}
              >
                Data Points{renderSortIndicator('dataPoints')}
              </th>
            </tr>
          </thead>
          <tbody>
            {trendData.map(model => (
              <tr key={model.id} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <td className="py-2">{model.name}</td>
                <td className="py-2">{model.provider}</td>
                <td 
                  className="text-right py-2"
                  title={`Raw value: ${model.lastPromptCost}`}
                >
                  {model.lastPromptPrice}
                </td>
                <td 
                  className="text-right py-2"
                  title={`Raw value: ${model.lastCompletionCost}`}
                >
                  {model.lastCompletionPrice}
                </td>
                <td 
                  className={`text-right py-2 ${
                    parseFloat(model.promptChange) > 0 
                      ? 'text-red-500' 
                      : parseFloat(model.promptChange) < 0 
                        ? 'text-green-500' 
                        : ''
                  }`}
                  title={`Previous: ${model.firstPromptPrice}, Current: ${model.lastPromptPrice}`}
                >
                  {model.isNewModel || model.dataPoints < 3 ? (
                    <span className="text-gray-400 italic">New model</span>
                  ) : model.promptChange === 0 ? (
                    "0.00%"
                  ) : (
                    `${model.promptChange > 0 ? '+' : ''}${model.promptChange.toFixed(2)}%`
                  )}
                </td>
                <td 
                  className={`text-right py-2 ${
                    parseFloat(model.completionChange) > 0 
                      ? 'text-red-500' 
                      : parseFloat(model.completionChange) < 0 
                        ? 'text-green-500' 
                        : ''
                  }`}
                  title={`Previous: ${model.firstCompletionPrice}, Current: ${model.lastCompletionPrice}`}
                >
                  {model.isNewModel || model.dataPoints < 3 ? (
                    <span className="text-gray-400 italic">New model</span>
                  ) : model.completionChange === 0 ? (
                    "0.00%"
                  ) : (
                    `${model.completionChange > 0 ? '+' : ''}${model.completionChange.toFixed(2)}%`
                  )}
                </td>
                <td className="text-right py-2">
                  {model.dataPoints}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-6">
        <h4 className="text-md font-medium mb-2">Price Information</h4>
        <div className="text-sm">
          <p>Prices shown are in USD per 1M tokens. Historical data is available for {Object.keys(trends.trends).length} models over the last {trends.days_analyzed} days.</p>
          <p className="mt-1">
            <span className="font-medium">Input prices:</span> Cost for sending tokens to the model (prompts)
          </p>
          <p className="mt-1">
            <span className="font-medium">Output prices:</span> Cost for receiving tokens from the model (completions)
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Note: Models with fewer than 3 data points are marked as "New model" as there isn't enough historical data for meaningful trend analysis.
          </p>
        </div>
      </div>
    </div>
  );
};
